import React, { useState, useEffect, useMemo, useCallback } from "react";
import { useNavigate, useParams } from "react-router-dom";
import "./external.css";
import PreviousSession from "./components/PreviousSession";
import Loader from "../../../components/Loader";
import { API_BASE_URL, authFetch } from "../../../config/api";
import { useCatalogue } from "../../../hooks/useCatalogue";

function OtherExternals() {
  const navigate = useNavigate();
  const { sesion } = useParams();
  const [load, setLoad] = useState(false);
  const [current_session, setCurrent_session] = useState(null);
  const [other_sessions, setOther_sessions] = useState([]);
  const [externals, setExternals] = useState([]);
  const [error, setError] = useState(null);
  const { getExternalForLevel } = useCatalogue();

  const levels_list = useMemo(() => [100, 200, 300], []);
  const first_semester = useMemo(
    () => levels_list.map((lev) => Object.keys(getExternalForLevel(lev, 1))),
    [levels_list, getExternalForLevel]
  );
  const second_semester = useMemo(
    () => levels_list.map((lev) => Object.keys(getExternalForLevel(lev, 2))),
    [levels_list, getExternalForLevel]
  );

  const fetchSessions = useCallback(() => {
    setLoad(true);
    authFetch(`${API_BASE_URL}/api/sessions/`)
      .then((res) => res.json())
      .then((json) => {
        setCurrent_session(json.sessions.find((s) => s.session === sesion) || null);
        setExternals(json.sessions.find((s) => s.session === sesion)?.externals || []);
        setOther_sessions(json.sessions.filter((s) => s.session !== sesion));
        setLoad(false);
      })
      .catch((err) => { setError(err.message); setLoad(false); });
  }, [sesion]);

  useEffect(() => { fetchSessions(); }, [fetchSessions]);

  return (
    <div className="cr_page">
      <div className="cr_header">
        <div>
          <p className="cr_overline">Academic Tools</p>
          <h2>External Courses</h2>
          <p className="cr_subtitle">View and manage external course results by session and level.</p>
        </div>
      </div>

      {load && <Loader />}

      {error && (
        <div className="cr_error">
          {error} — <button className="cr_retry" onClick={fetchSessions}>Retry</button>
        </div>
      )}

      {!load && !error && !current_session && (
        <p className="cr_empty">Session not found.</p>
      )}

      {current_session && (
        <div className="cr_section">
          <div className="cr_section_head">
            <span className="cr_section_label">Session</span>
            <span className="cr_session_badge">{current_session.session}</span>
          </div>

          <div className="ext_grid">
            <div className="ext_col">
              <p className="ext_sem_label">First Semester</p>
              {levels_list.map((lev, i) => (
                <div key={lev} className="ext_level_block">
                  <span className="ext_level_tag">{lev} Level</span>
                  <div className="ext_course_row">
                    {first_semester[i].length > 0 ? first_semester[i].map((code) => (
                      <button
                        key={code}
                        className="ext_course_btn"
                        onClick={() => navigate(`/admin/faculty/${sesion}/${lev}/1/${code}`)}
                      >
                        {code.toUpperCase()}
                      </button>
                    )) : <span className="cr_empty">—</span>}
                  </div>
                </div>
              ))}
            </div>

            <div className="ext_col">
              <p className="ext_sem_label">Second Semester</p>
              {levels_list.map((lev, i) => (
                <div key={lev} className="ext_level_block">
                  <span className="ext_level_tag">{lev} Level</span>
                  <div className="ext_course_row">
                    {second_semester[i].length > 0 ? second_semester[i].map((code) => (
                      <button
                        key={code}
                        className="ext_course_btn"
                        onClick={() => navigate(`/admin/faculty/${sesion}/${lev}/2/${code}`)}
                      >
                        {code.toUpperCase()}
                      </button>
                    )) : <span className="cr_empty">—</span>}
                  </div>
                </div>
              ))}
            </div>
          </div>

          {externals.length > 0 && (
            <div className="cr_section" style={{ marginTop: "1.5rem" }}>
              <div className="cr_section_head">
                <span className="cr_section_label">Manually Added</span>
              </div>
              <div className="ext_course_row">
                {externals.map((ext, i) => (
                  <button
                    key={i}
                    className="ext_course_btn"
                    onClick={() => navigate(`/admin/faculty/${current_session.session}/${ext.semester}/${ext.course_code}/${ext.course_title}/${ext.unit_load}`)}
                  >
                    {ext.course_code.toUpperCase()}
                  </button>
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      {other_sessions.length > 0 && (
        <div className="cr_section">
          <div className="cr_section_head">
            <span className="cr_section_label">Other Sessions</span>
          </div>
          <div className="cr_session_list">
            {other_sessions.map((s) => (
              <PreviousSession key={s.session} session={s.session} />
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

export default OtherExternals;
